<template>
  <b-card
    no-body
    class="announcement-container mb-0"
    :class="{ 'announcement-feed': dense, 'h-100': dense }"
  >
    <b-card-header
      class="d-flex align-items-center justify-content-between border-bottom"
      :class="dense ? 'py-50 px-1' : 'py-1 px-1'"
    >
      <h6 class="mb-0 d-flex align-items-center text-body announcement-title-heading">
        <feather-icon
          icon="BellIcon"
          :size="dense ? '13' : '18'"
          class="mr-50 text-primary"
        />
        {{ dense ? 'Announcements' : 'Announcement' }}
      </h6>
      <div class="d-flex align-items-center">
        <b-button
          v-if="canDownload"
          size="sm"
          variant="outline-success"
          class="announcement-action-btn mr-50"
          aria-label="Download"
          @click="$emit('download')"
        >
          <feather-icon icon="DownloadIcon" :size="dense ? '12' : '14'" />
        </b-button>
        <b-button
          v-if="canCreate"
          size="sm"
          :variant="dense ? 'outline-primary' : 'primary'"
          class="announcement-action-btn"
          :class="{ 'announcement-action-btn--labeled': !!createLabel }"
          :aria-label="createLabel || 'Add Announcement'"
          @click="$emit('create-announcement')"
        >
          <feather-icon icon="PlusIcon" :size="dense ? '12' : '14'" />
          <span v-if="createLabel" class="announcement-create-label ml-25">{{ createLabel }}</span>
        </b-button>
      </div>
    </b-card-header>

    <b-card-body class="d-flex flex-column announcement-body" :class="dense ? 'p-1' : 'p-1'">
      <template v-if="allAnnouncements.length > 0">
        <b-list-group flush class="announcements-list flex-grow-1">
          <b-list-group-item
            v-for="(item, index) in allAnnouncements"
            :key="item.id || item.emp_id || `announcement-${index}`"
            class="announcement-item px-50 border-0"
            :class="[
              getAnnouncementClass(item.type),
              dense ? 'py-25 mb-50' : 'py-1 mb-1',
            ]"
          >
            <b-media>
              <template #aside>
                <div
                  class="announcement-badge d-flex align-items-center justify-content-center"
                  :class="getIconClass(item.type)"
                >
                  <feather-icon :icon="getIcon(item.type)" :size="dense ? '11' : '14'" />
                </div>
              </template>
              <div class="d-flex justify-content-between align-items-start">
                <div class="announcement-item-title font-weight-bolder text-truncate pr-50">
                  {{ item.title }}
                </div>
                <b-button
                  v-if="typeof canCancel === 'function' && canCancel(item) && item.type === 'announcement'"
                  variant="link"
                  size="sm"
                  class="p-0 ml-50 flex-shrink-0"
                  aria-label="Cancel announcement"
                  @click="$emit('cancel-announcement', item.id)"
                >
                  <feather-icon icon="XIcon" :size="dense ? '14' : '16'" class="text-danger" />
                </b-button>
              </div>
              <div class="announcement-message text-muted">{{ item.message }}</div>
              <div class="announcement-date d-flex align-items-center text-muted">
                <feather-icon icon="CalendarIcon" size="11" class="mr-25" />
                {{ getDisplayDate(item) }}
              </div>
            </b-media>
          </b-list-group-item>
        </b-list-group>
      </template>
      <div
        v-else
        class="announcement-empty-state d-flex align-items-center justify-content-center text-muted flex-grow-1"
        :class="{ 'announcement-empty-dense flex-row': dense, 'flex-column': !dense }"
      >
        <feather-icon icon="BellOffIcon" :size="dense ? '14' : '32'" :class="dense ? 'mr-50' : 'mb-50'" />
        <p class="mb-0">{{ canCreate ? 'No announcements yet' : 'No announcements' }}</p>
        <b-button
          v-if="canCreate && createLabel"
          size="sm"
          variant="outline-primary"
          class="announcement-action-btn announcement-action-btn--labeled ml-50"
          @click="$emit('create-announcement')"
        >
          <feather-icon icon="PlusIcon" size="12" />
          <span class="announcement-create-label ml-25">{{ createLabel }}</span>
        </b-button>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BListGroup,
  BListGroupItem,
  BMedia,
} from 'bootstrap-vue'

export default {
  name: 'AnnouncementsCard',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BListGroup,
    BListGroupItem,
    BMedia,
  },
  props: {
    announcements: { type: Array, default: () => [] },
    totalCount: { type: Number, default: 0 },
    canCreate: { type: Boolean, default: false },
    /** When set (e.g. Org/HR), show labeled "+ Add Announcement" instead of icon-only */
    createLabel: { type: String, default: '' },
    canDownload: { type: Boolean, default: false },
    canCancel: { type: Function, default: () => false },
    formatDate: { type: Function, required: true },
    formatDateMonthOnly: { type: Function, required: true },
    downloadDurationLabel: { type: String, default: '' },
    dense: { type: Boolean, default: false },
  },
  computed: {
    allAnnouncements() {
      return Array.isArray(this.announcements) ? this.announcements : []
    },
    downloadTooltip() {
      if (this.downloadDurationLabel) {
        return `Download Birthdays & Anniversaries (${this.downloadDurationLabel})`
      }
      return 'Download Birthdays & Anniversaries'
    },
  },
  methods: {
    getAnnouncementClass(type) {
      if (type === 'birthday') return 'announcement-birthday'
      if (type === 'anniversary') return 'announcement-anniversary'
      return 'announcement-default'
    },
    getIconClass(type) {
      if (type === 'birthday') return 'badge-birthday'
      if (type === 'anniversary') return 'badge-anniversary'
      return 'badge-default'
    },
    getIcon(type) {
      if (type === 'birthday') return 'HeartIcon'
      if (type === 'anniversary') return 'AwardIcon'
      return 'InfoIcon'
    },
    getDisplayDate(item) {
      try {
        if (item.type === 'birthday' || item.type === 'anniversary') {
          return this.formatDateMonthOnly
            ? this.formatDateMonthOnly(item.birthday_date || item.anniversary_date || item.date)
            : (item.birthday_date || item.anniversary_date || item.date || '')
        }
        return this.formatDate
          ? this.formatDate(item.created_at || item.date)
          : (item.created_at || item.date || '')
      } catch (e) {
        return item.created_at || item.date || ''
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.announcement-container {
  height: 350px;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
}

.announcement-container.announcement-feed {
  height: auto;
  min-height: 0;
  max-height: 180px;
  border: 1px solid #e8ecf0;
  box-shadow: none;
  border-radius: 10px;
  overflow: hidden;
}

.announcement-container :deep(.card-header) {
  background: #fff;
  flex-shrink: 0;
}

.announcement-title-heading {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
}

.announcement-feed .announcement-title-heading {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
}

.announcement-action-btn {
  padding: 0.15rem 0.4rem;
  line-height: 1.2;
}

.announcement-action-btn--labeled {
  padding: 0.15rem 0.5rem;
  white-space: nowrap;
}

.announcement-create-label {
  font-size: 0.68rem;
  font-weight: 600;
}

.announcement-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.announcements-list {
  min-height: 0;
  max-height: 280px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.announcement-feed .announcements-list {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
}

.announcements-list::-webkit-scrollbar {
  width: 4px;
}

.announcements-list::-webkit-scrollbar-thumb {
  background: #94a3b8;
  border-radius: 10px;
}

.announcement-item {
  background: #fafbfc !important;
  border: 1px solid #f0f2f5 !important;
  border-left: 3px solid #0099AC !important;
  border-radius: 6px !important;
}

.announcement-birthday {
  border-left-color: #ec4899 !important;
}

.announcement-anniversary {
  border-left-color: #f59e0b !important;
}

/* Team dense feed: no left accent bars */
.announcement-feed .announcement-item,
.announcement-feed .announcement-birthday,
.announcement-feed .announcement-anniversary {
  border-left: 1px solid #f0f2f5 !important;
}

.announcement-badge {
  min-width: 36px;
  height: 36px;
  background: rgba(0, 153, 172, 0.12);
  border-radius: 8px;
  color: #0099AC;
}

.announcement-feed .announcement-badge {
  min-width: 20px;
  height: 20px;
  border-radius: 5px;
}

.badge-birthday {
  background: rgba(236, 72, 153, 0.12) !important;
  color: #db2777 !important;
}

.badge-anniversary {
  background: rgba(245, 158, 11, 0.12) !important;
  color: #d97706 !important;
}

.announcement-item-title {
  font-size: 0.95rem;
  color: #2c3e50;
  line-height: 1.3;
}

.announcement-feed .announcement-item-title {
  font-size: 0.7rem;
}

.announcement-message {
  font-size: 0.85rem;
  line-height: 1.4;
  margin-top: 0.15rem;
  word-wrap: break-word;
}

.announcement-feed .announcement-message {
  font-size: 0.62rem;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.announcement-date {
  font-size: 0.75rem;
  margin-top: 0.25rem;
  gap: 0.15rem;
}

.announcement-feed .announcement-date {
  font-size: 0.58rem;
  margin-top: 0.1rem;
}

.announcement-empty-state {
  min-height: 100px;
  text-align: center;
  padding: 1.25rem 0.5rem;
}

.announcement-empty-state p {
  font-size: 0.85rem;
}

.announcement-empty-dense {
  min-height: 28px;
  max-height: 40px;
  padding: 0.25rem;
  gap: 0.25rem;
}

.announcement-empty-dense p {
  font-size: 0.65rem;
  white-space: nowrap;
}
</style>
